import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, Anchor, H3 } from '@zooplus/ui-components';

import { captureException } from '../../services/sentryService';
import { useCreditcardService } from '../CreditCardsMutationProvider';
import { useErrorContext } from '../ErrorContextProvider';
import { OverlayIds, useOverlays } from '../OverlaysProvider';

import CreditCard from './CreditCard';
import { CreditCardData } from './index.interfaces';
import NewCardActionButton from './NewCardActionButton';
import styles from './StoredCreditCards.module.scss';

export type StoredCreditCardsProps = {
  isMyAccount: boolean;
  isFallbackPsp: boolean;
};

const StoredCreditCards: React.FC<StoredCreditCardsProps> = ({ isMyAccount, isFallbackPsp }) => {
  const { showOverlay } = useOverlays();
  const { t } = useTranslation();
  const { setError } = useErrorContext();
  const [loadingCardPspIdentifier, setLoadingCardPspIdentifier] = useState<string>();

  const {
    initiateCardDeletion,
    makeCreditCardActive,
    data: { creditCards },
  } = useCreditcardService();

  const handleAddCard = () => {
    showOverlay(OverlayIds.ADD_NEW_CARD);
  };

  const handleSelectCard = (creditCard: CreditCardData) => () => {
    if (!creditCard.selected) {
      if (creditCard.expired) {
        setError({
          message: t('checkout.payment.creditcard.error.selectcard.expired'),
          retryable: true,
        });
      } else {
        setError(undefined);
        setLoadingCardPspIdentifier(creditCard.pspIdentifier);

        makeCreditCardActive(creditCard)
          .then((isSuccess) => {
            if (!isSuccess) {
              setError({
                message: t('checkout.payment.creditcard.error.selectcard.apicall'),
                retryable: true,
              });
            }
            setLoadingCardPspIdentifier(undefined);
          })
          .catch((error: unknown) => {
            // on any unexpected error, send it to Sentry and display a generic error message to the user
            captureException({ error });
            setError({
              message: t('checkout.payment.creditcard.error.selectcard.apicall'),
              retryable: true,
            });
            setLoadingCardPspIdentifier(undefined);
          });
      }
    }
  };

  const handleDeleteCard =
    (creditCard: CreditCardData) => (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      initiateCardDeletion(creditCard);
    };

  const handleDeleteAllCards = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    showOverlay(OverlayIds.DELETE_ALL_CARDS);
  };

  return (
    <div className={styles.container}>
      {isMyAccount && !isFallbackPsp && (
        <Alert className={styles.messageCanAddCard} variant="info" data-zta="messageCanAddCard">
          {t('mzp.payment.adyen.no.card.message')}
        </Alert>
      )}
      {(creditCards.length > 0 || !isMyAccount) && (
        <>
          <H3 className={styles.heading}>{t('checkout.payment.creditcard.storedcards.header')}</H3>
          <div className={styles.creditCards}>
            {creditCards.map((creditCard) => (
              <CreditCard
                key={creditCard.pspIdentifier}
                creditCard={creditCard}
                onDelete={handleDeleteCard(creditCard)}
                onSelect={handleSelectCard(creditCard)}
                showIcons
                loadingCardPspIdentifier={loadingCardPspIdentifier}
              />
            ))}
            {!isMyAccount && <NewCardActionButton onClick={handleAddCard} />}
            <div className={styles.creditCardSpacer} />
          </div>
          <div className={styles.deleteAll}>
            <Anchor
              className={styles.grayLink}
              data-zta="removeAllCC"
              onClick={handleDeleteAllCards}>
              {t('checkout.payment.creditcard.deleteAll')}
            </Anchor>
          </div>
        </>
      )}
    </div>
  );
};

export default StoredCreditCards;
